import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  Truck, Save, Code, MapPin, ExternalLink, 
  Check, Copy, Loader2, AlertCircle 
} from 'lucide-react';

// --- STATIC DATA FOR INDIA ---
const INDIAN_LOCATIONS: Record<string, string[]> = {
  "Andhra Pradesh": ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool", "Rajahmundry", "Tirupati"],
  "Arunachal Pradesh": ["Itanagar", "Naharlagun"],
  "Assam": ["Guwahati", "Silchar", "Dibrugarh", "Jorhat", "Nagaon", "Tinsukia"],
  "Bihar": ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Purnia", "Darbhanga"],
  "Chhattisgarh": ["Raipur", "Bhilai", "Bilaspur", "Korba", "Durg"],
  "Delhi": ["New Delhi", "Delhi", "Noida", "Gurgaon", "Ghaziabad", "Faridabad"], 
  "Goa": ["Panaji", "Margao", "Vasco da Gama", "Mapusa"],
  "Gujarat": ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar", "Gandhinagar"],
  "Haryana": ["Gurgaon", "Faridabad", "Panipat", "Ambala", "Yamunanagar", "Rohtak"],
  "Himachal Pradesh": ["Shimla", "Dharamshala", "Mandi", "Solan"],
  "Jharkhand": ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro"],
  "Karnataka": ["Bangalore", "Mysore", "Hubli-Dharwad", "Mangalore", "Belgaum", "Gulbarga"],
  "Kerala": ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur", "Kollam", "Kannur"],
  "Madhya Pradesh": ["Indore", "Bhopal", "Jabalpur", "Gwalior", "Ujjain"],
  "Maharashtra": ["Mumbai", "Pune", "Nagpur", "Thane", "Nashik", "Aurangabad", "Solapur", "Kolhapur", "Navi Mumbai"],
  "Manipur": ["Imphal"],
  "Meghalaya": ["Shillong"],
  "Mizoram": ["Aizawl"],
  "Nagaland": ["Dimapur", "Kohima"],
  "Odisha": ["Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Sambalpur"],
  "Punjab": ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Bathinda"],
  "Rajasthan": ["Jaipur", "Jodhpur", "Kota", "Bikaner", "Ajmer", "Udaipur"],
  "Sikkim": ["Gangtok"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem", "Tirunelveli"],
  "Telangana": ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar"],
  "Tripura": ["Agartala"],
  "Uttar Pradesh": ["Lucknow", "Kanpur", "Ghaziabad", "Agra", "Varanasi", "Meerut", "Prayagraj", "Noida"],
  "Uttarakhand": ["Dehradun", "Haridwar", "Roorkee", "Haldwani"],
  "West Bengal": ["Kolkata", "Howrah", "Durgapur", "Asansol", "Siliguri"],
  "Chandigarh": ["Chandigarh"],
  "Jammu and Kashmir": ["Srinagar", "Jammu"],
  "Ladakh": ["Leh"],
  "Puducherry": ["Puducherry"]
};

const DeliveryTimelineTab = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  // FIX 1: Store User ID in state so we can fill it if the prop is missing
  const [currentUserId, setCurrentUserId] = useState(user?.id || null);

  // Default State
  const [settings, setSettings] = useState({
    is_active: true,
    warehouse_city: "New Delhi",
    warehouse_state: "Delhi",
    processing_time: 1,
    days_city: 2,
    days_state: 3,
    days_metro: 4,
    days_national: 7,
    store_url: user?.admin_website || ""
  });

  useEffect(() => {
    // FIX 2: Recover User ID if missing, then fetch settings
    const initData = async () => {
        let activeId = user?.id;

        // If prop is empty, try to get from active session
        if (!activeId) {
            const { data } = await supabase.auth.getUser();
            activeId = data?.user?.id;
        }

        if (activeId) {
            setCurrentUserId(activeId);
            fetchSettings(activeId);
        } else {
            console.error("No user found in prop or session.");
            setLoading(false);
        }
    };

    initData();
  }, [user]);

  const fetchSettings = async (userId: string) => {
    try {
      setLoading(true);
      setErrorMsg(null);
      
      const { data, error } = await supabase
        .from('dt_settings')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        // Ignore "Row not found" (PGRST116) - it just means new settings
        if (error.code !== 'PGRST116') {
            throw error;
        }
      }

      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
      
    } catch (error: any) {
      console.error("Error fetching settings:", error);
      setErrorMsg(error.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // FIX 3: Use the robust currentUserId instead of just the prop
    if (!currentUserId) {
        return alert("User ID not found. Please refresh the page.");
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('dt_settings')
        .upsert({
          user_id: currentUserId,
          ...settings,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;
      alert("Settings saved successfully!");
    } catch (error: any) {
      console.error("Save Error:", error);
      alert(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const openShopifyEditor = () => {
    let url = settings.store_url || user?.admin_website;
    if (!url) return alert("Please enter your Store URL first.");
    
    // Clean URL
    url = url.replace('https://', '').replace('http://', '').replace(/\/$/, '');
    window.open(`https://${url}/admin/themes/current/editor`, '_blank');
  };

  const copyCode = () => {
    const code = `<script src="https://powerpack.techsoftwares.in/widget.js?id=${currentUserId || 'MISSING_ID'}" defer></script>`;
    navigator.clipboard.writeText(code);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // --- RENDER ---

  if (loading) return (
      <div className="p-12 flex flex-col items-center justify-center text-gray-500">
          <Loader2 className="animate-spin mb-3 w-8 h-8 text-indigo-500" />
          <p>Loading delivery settings...</p>
      </div>
  );

  if (errorMsg) return (
      <div className="p-8 text-center text-red-500 border border-red-100 rounded-xl bg-red-50 m-4">
          <AlertCircle className="mx-auto mb-2 w-8 h-8" />
          <p className="font-bold">Unable to load settings</p>
          <p className="text-sm opacity-80 mb-4">{errorMsg}</p>
          <button 
            onClick={() => currentUserId && fetchSettings(currentUserId)} 
            className="px-4 py-2 bg-white border border-red-200 rounded-lg text-red-600 hover:bg-red-50 text-sm font-medium"
          >
            Retry
          </button>
      </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-10">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="text-indigo-600" /> Delivery Timeline
          </h2>
          <p className="text-gray-500 text-sm mt-1">Configure delivery estimates based on customer location.</p>
        </div>
        
        <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
            <label className="flex items-center gap-2 cursor-pointer bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 select-none">
                <span className="text-sm font-medium text-gray-700">Widget Status</span>
                <div className={`w-10 h-5 rounded-full relative transition-colors ${settings.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
                <input 
                    type="checkbox" 
                    className="hidden" 
                    checked={settings.is_active}
                    onChange={e => setSettings({...settings, is_active: e.target.checked})}
                />
                <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform ${settings.is_active ? 'translate-x-5' : ''}`} />
                </div>
            </label>

            <button 
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 transition-all"
            >
                {saving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>}
                Save
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* 1. WAREHOUSE LOCATION */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4 h-fit">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 border-b pb-2">
            <MapPin size={18} className="text-gray-500"/> Warehouse Location
          </h3>
          <p className="text-xs text-gray-400">Used to calculate faster delivery for local customers.</p>
          
          <div className="grid grid-cols-2 gap-4">
            {/* STATE DROPDOWN */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">State</label>
              <select 
                value={settings.warehouse_state}
                onChange={e => {
                    const newState = e.target.value;
                    // Reset city when state changes to avoid mismatches
                    setSettings({
                        ...settings, 
                        warehouse_state: newState, 
                        warehouse_city: "" 
                    });
                }}
                className="w-full border border-gray-200 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 outline-none bg-white transition-shadow"
              >
                <option value="" disabled>Select State</option>
                {Object.keys(INDIAN_LOCATIONS).sort().map(state => (
                    <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* CITY DROPDOWN */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                 City <span className="text-[10px] font-normal text-gray-400 lowercase">(nearest hub)</span>
              </label>
              <select 
                value={settings.warehouse_city}
                onChange={e => setSettings({...settings, warehouse_city: e.target.value})}
                disabled={!settings.warehouse_state} 
                className="w-full border border-gray-200 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 outline-none bg-white transition-shadow disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="" disabled>
                    {settings.warehouse_state ? "Select City" : "Select State First"}
                </option>
                
                {settings.warehouse_state && INDIAN_LOCATIONS[settings.warehouse_state]?.map(city => (
                    <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Processing Time (Days)</label>
              <input 
                type="number" min="0"
                value={settings.processing_time}
                onChange={e => setSettings({...settings, processing_time: parseInt(e.target.value) || 0})}
                className="w-full border border-gray-200 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow"
              />
              <p className="text-xs text-gray-400 mt-1">How long it takes you to pack and ship the order.</p>
          </div>
        </div>

        {/* 2. DELIVERY RULES */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4 h-fit">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 border-b pb-2">
            <Truck size={18} className="text-gray-500"/> Estimated Transit Time
          </h3>
          
          <div className="space-y-4">
             <div className="flex items-center justify-between">
                <label className="text-sm text-gray-600">Same City</label>
                <div className="flex items-center gap-2">
                    <input 
                        type="number" min="0" className="w-16 border rounded p-1 text-center focus:ring-2 focus:ring-indigo-500 outline-none"
                        value={settings.days_city}
                        onChange={e => setSettings({...settings, days_city: parseInt(e.target.value) || 0})}
                    />
                    <span className="text-xs text-gray-400 w-8">days</span>
                </div>
             </div>

             <div className="flex items-center justify-between">
                <label className="text-sm text-gray-600">Same State</label>
                <div className="flex items-center gap-2">
                    <input 
                        type="number" min="0" className="w-16 border rounded p-1 text-center focus:ring-2 focus:ring-indigo-500 outline-none"
                        value={settings.days_state}
                        onChange={e => setSettings({...settings, days_state: parseInt(e.target.value) || 0})}
                    />
                    <span className="text-xs text-gray-400 w-8">days</span>
                </div>
             </div>

             <div className="flex items-center justify-between">
                <label className="text-sm text-gray-600">Metro Cities</label>
                <div className="flex items-center gap-2">
                    <input 
                        type="number" min="0" className="w-16 border rounded p-1 text-center focus:ring-2 focus:ring-indigo-500 outline-none"
                        value={settings.days_metro}
                        onChange={e => setSettings({...settings, days_metro: parseInt(e.target.value) || 0})}
                    />
                    <span className="text-xs text-gray-400 w-8">days</span>
                </div>
             </div>

             <div className="flex items-center justify-between bg-indigo-50 p-2 rounded-lg -mx-2">
                <label className="text-sm font-bold text-indigo-900">Rest of India (National)</label>
                <div className="flex items-center gap-2">
                    <input 
                        type="number" min="0" className="w-16 border border-indigo-200 bg-white rounded p-1 text-center font-bold text-indigo-700 focus:ring-2 focus:ring-indigo-500 outline-none"
                        value={settings.days_national}
                        onChange={e => setSettings({...settings, days_national: parseInt(e.target.value) || 0})}
                    />
                    <span className="text-xs text-indigo-400 w-8">days</span>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* 3. INSTALLATION */}
      <div className="bg-slate-900 text-white p-8 rounded-xl shadow-lg mt-8">
        <div className="flex flex-col md:flex-row justify-between items-start mb-6 gap-4">
            <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-2">
                <Code size={20} className="text-green-400" /> Installation
                </h3>
                <p className="text-slate-400 text-sm">
                    Follow these steps to add the widget to your store.
                </p>
            </div>
            
            <div className="flex flex-col gap-2 items-end w-full md:w-auto">
                <input 
                    type="text" 
                    placeholder="yourstore.myshopify.com"
                    value={settings.store_url}
                    onChange={e => setSettings({...settings, store_url: e.target.value})}
                    className="bg-slate-800 border border-slate-700 text-white text-xs px-3 py-2 rounded w-full md:w-64 mb-1 focus:border-green-500 outline-none"
                />
                <button 
                    onClick={openShopifyEditor}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-green-500 flex items-center justify-center gap-2 transition-colors w-full md:w-auto"
                >
                    <ExternalLink size={16} /> Open Theme Editor
                </button>
            </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Code Snippet */}
            <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase">Step 1: Copy Code</label>
                <div className="bg-black p-4 rounded-lg font-mono text-xs text-green-400 break-all relative group border border-slate-700">
                    {`<script src="https://powerpack.techsoftwares.in/widget.js?id=${currentUserId || 'MISSING_ID'}" defer></script>`}
                    
                    <button 
                        onClick={copyCode}
                        className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 text-white p-2 rounded transition-colors"
                        title="Copy to clipboard"
                    >
                        {copySuccess ? <Check size={14} className="text-green-400"/> : <Copy size={14}/>}
                    </button>
                </div>
            </div>

            {/* Instructions */}
            <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase">Step 2: Install</label>
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 text-sm text-slate-300 space-y-3">
                    <div className="flex gap-3">
                        <span className="bg-slate-700 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold">1</span>
                        <span>Click the <strong>Open Theme Editor</strong> button above.</span>
                    </div>
                    <div className="flex gap-3">
                        <span className="bg-slate-700 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold">2</span>
                        <span>In the sidebar, find <strong>Theme Liquid</strong> (or Footer).</span>
                    </div>
                    <div className="flex gap-3">
                        <span className="bg-slate-700 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold">3</span>
                        <span>Paste the code right before the closing <code className="text-red-300">&lt;/body&gt;</code> tag.</span>
                    </div>
                    <div className="flex gap-3">
                        <span className="bg-slate-700 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold">4</span>
                        <span>Click <strong>Save</strong>. The widget will appear automatically.</span>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryTimelineTab;
